import type { VolumeChartData } from './volume-chart';

export type IndexedVolumeData = {
  active: Map<number, number>;
  new: Map<number, number>;
  removed: Map<number, number>;
  weekDates: Date[];
};

// Create an indexed version of the volume data for efficient lookups on mouse move
export const createIndexedVolumeData = (
  data: VolumeChartData[]
): IndexedVolumeData => {
  const weekDatesSet = new Set<number>();
  const activeMap = new Map<number, number>();
  const newMap = new Map<number, number>();
  const removedMap = new Map<number, number>();

  data.forEach((series) => {
    series.values.forEach(({ date, value }) => {
      const weekStartTimestamp = new Date(date).getTime();

      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      if (!isNaN(weekStartTimestamp)) {
        weekDatesSet.add(weekStartTimestamp);

        switch (series.id) {
          case 'active':
            activeMap.set(weekStartTimestamp, value);
            break;
          case 'new':
            newMap.set(weekStartTimestamp, value);
            break;
          case 'removed':
            removedMap.set(weekStartTimestamp, value);
            break;
        }
      }
    });
  });

  const weekDates = Array.from(weekDatesSet)
    .map((time) => new Date(time))
    .sort((a, b) => a.getTime() - b.getTime());

  const sortedActive = Array.from(activeMap.entries()).sort(
    (a, b) => a[0] - b[0]
  );
  const sortedNew = Array.from(newMap.entries()).sort((a, b) => a[0] - b[0]);
  const sortedRemoved = Array.from(removedMap.entries()).sort(
    (a, b) => a[0] - b[0]
  );

  return {
    active: new Map(sortedActive),
    new: new Map(sortedNew),
    removed: new Map(sortedRemoved),
    weekDates,
  };
};

// Get data for a specific week
export const getWeekData = (
  indexedData: IndexedVolumeData,
  weekStartTimestamp: number
): { active: number; newPostings: number; removed: number } => {
  return {
    active: indexedData.active.get(weekStartTimestamp) || 0,
    newPostings: indexedData.new.get(weekStartTimestamp) || 0,
    removed: indexedData.removed.get(weekStartTimestamp) || 0,
  };
};
